import React, { useState, useEffect, useMemo } from "react";
import useUserStore from "../../@store/useUserStore";
import SubHeader from "components/common/SubHeader";
import AttendanceCard from "components/admin/AttendanceCard";
import Tab from "components/Tab";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import "dayjs/locale/ko";
import "./AdminAttendance.scss";
import { getAttendanceScheduleList } from "api/attendance";
import { useSearchParams , useNavigate} from "react-router-dom";
import WarningBanner from "components/common/WarningBanner";
import AddIcon from "@mui/icons-material/Add";
import ViewListIcon from "@mui/icons-material/ViewList";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
// import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Calendar from "react-calendar";
dayjs.extend(isBetween);
dayjs.locale("ko");

const WEEKDAYS_KR = ["일", "월", "화", "수", "목", "금", "토"];

const getSchedulesByDate = (schedules, date) =>
    schedules.filter((classItem) => dayjs(classItem.date).isSame(date, "day"));

const AdminAttendance = () => {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams();
    const userInfo = useUserStore((state) => state.userInfo);
    const [classList, setClassList] = useState([]);
    const [activeTab, setActiveTab] = useState(
        Number(searchParams.get("tab")) || 1
    );
    const [viewType, setViewType] = useState(
        searchParams.get("view") === "calendar" ? "calendar" : "list"
    );
    const [selectedDate, setSelectedDate] = useState(dayjs().toDate());
    const [activeStartDate, setActiveStartDate] = useState(
        dayjs().startOf("month").toDate()
    );
    const [targetMonth, setTargetMonth] = useState(dayjs().format("YYYY-MM"));
    const [targetInstructor, setTargetInstructor] = useState(userInfo.name);

    const refreshScheduleList = () => {
        getAttendanceScheduleList().then((res) => {
            const sorted = [...res].sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
            setClassList(sorted);
        });
    };

    useEffect(() => {
        refreshScheduleList();
    }, [userInfo.user_id]);

    // 출석 처리 필요 / 완료 분류
    const { pendingClasses, completedClasses, upcomingClasses } = useMemo(() => {
        const pending = [];
        const completed = [];
        const upcoming = [];
        const today = dayjs().startOf("day");

        classList.forEach((classItem) => {
            const hasSessionStudents = (classItem.session?.student_count ?? 0) > 0;
            const hasPracticeStudents = (classItem.practice?.student_count ?? (classItem.type === "practice" ? classItem.student_count : 0) ?? 0) > 0;
            const hasStudents = hasSessionStudents || hasPracticeStudents;
            const classDate = dayjs(classItem.date);
            const isTodayOrFuture = !classDate.isBefore(today, "day");

            // 강습 출석 완료 여부 (is_attendance_completed 또는 최상위 status가 CLOSED이면 완료)
            const isSessionCompleted = !hasSessionStudents || classItem.is_attendance_completed || classItem.status === "CLOSED";
            // 연습반 정산 완료 여부 (practice 객체에 status가 없으므로 type=practice일 때 최상위 status 사용)
            const practiceStatus = classItem.practice?.status ?? (classItem.type === "practice" ? classItem.status : undefined);
            const isPracticeCompleted = !hasPracticeStudents || practiceStatus === "CLOSED";
            const isAllCompleted = isSessionCompleted && isPracticeCompleted;

            // 과거 완료된 스케줄은 학생 수 0이어도 완료 탭에 표시
            const isPast = classDate.isBefore(today, "day");
            if (isAllCompleted && (hasStudents || isPast)) {
                completed.push(classItem);
            } else {
                if (isTodayOrFuture) {
                    upcoming.push(classItem);
                }
                if (!isAllCompleted && hasStudents) {
                    pending.push(classItem);
                }
            }
        });

        return {
            pendingClasses: pending,
            completedClasses: completed,
            upcomingClasses: upcoming,
        };
    }, [classList]);

    // 완료 탭에서 월별, 강사별 필터링
    const filteredCompletedClasses = useMemo(() => {
        if (activeTab !== 3) return completedClasses;

        return completedClasses.filter((classItem) => {
            const classDate = dayjs(classItem.date);
            const targetDate = dayjs(targetMonth);
            const monthMatch = classDate.isSame(targetDate, "month");
            if (targetInstructor) {
                return monthMatch && classItem.instructor_name.includes(targetInstructor);
            }
            return monthMatch;
        });
    }, [completedClasses, targetMonth, activeTab, targetInstructor]);

    // 캘린더 뷰: 완료된 스케줄 id 집합 (카드별 isCompleted 판정용)
    const completedIds = useMemo(
        () => new Set(completedClasses.map((c) => c.id)),
        [completedClasses]
    );

    // 캘린더 뷰: 선택한 날짜의 스케줄 (탭 구분 없이 통합, 시간순)
    const selectedDaySchedules = useMemo(
        () =>
            getSchedulesByDate(classList, selectedDate)
                .slice()
                .sort((a, b) =>
                    (a.start_time ?? "").localeCompare(b.start_time ?? "")
                ),
        [classList, selectedDate]
    );

    // 월 선택 옵션 생성 (과거 6개월 + 현재 + 미래 6개월)
    const monthOptions = useMemo(() => {
        const options = [];
        const now = dayjs();

        for (let i = -6; i <= 6; i++) {
            const month = now.add(i, "month");
            options.push({
                value: month.format("YYYY-MM"),
                label: month.format("YYYY년 MM월"),
            });
        }

        return options;
    }, []);

    const tabs = [
        { id: 1, name: `전체 (${upcomingClasses.length})`, dot: false },
        { id: 2, name: `미완료 (${pendingClasses.length})`, dot: pendingClasses.length > 0 },
        { id: 3, name: `완료 (${filteredCompletedClasses.length})`, dot: false },
    ];

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        const next = new URLSearchParams(searchParams);
        next.set("tab", tabId.toString());
        setSearchParams(next, { replace: true });
    };

    const handleViewChange = (view) => {
        setViewType(view);
        const next = new URLSearchParams(searchParams);
        next.set("view", view);
        setSearchParams(next, { replace: true });
    };

    const getDisplayedClasses = () => {
        if (activeTab === 2) return pendingClasses;
        if (activeTab === 3) return filteredCompletedClasses;
        return upcomingClasses;
    };

    const displayedClasses = getDisplayedClasses();

    // 월 선택 핸들러
    const handleMonthChange = (e) => {
        setTargetMonth(e.target.value);
    };

    const handleInstructorChange = (e) => {
        setTargetInstructor(e.target.value);
    };
    return (
        <>
            <SubHeader
                title="스케쥴 관리"
                menus={[
                    {
                        name: "월별 통계",
                        type: "button",
                        callback: () => navigate("/instructor/stats"),
                    },
                ]}
            />
            <div className="admin-attendance-container">
                {/* Command Center: Current context summary and high-end segmented control switcher */}
                <div className="command-center">
                    <div className="current-context">
                        <span className="admin-label">Admin Dashboard</span>
                        <div className="date-selector">
                            <span className="date-text">
                                {viewType === "list"
                                    ? dayjs(targetMonth).format("YYYY년 MM월")
                                    : dayjs(activeStartDate).format("YYYY년 MM월")}
                            </span>
                            {/* <ExpandMoreIcon className="chevron-icon" /> */}
                        </div>
                    </div>
                    
                    <div className="view-switcher" role="radiogroup" aria-label="조회 방식 선택">
                        <div className={`switcher-bg ${viewType}`} aria-hidden="true"></div>
                        <button 
                            type="button" 
                            className={`switcher-btn ${viewType === 'list' ? 'active' : ''}`}
                            onClick={() => handleViewChange('list')}
                            role="radio"
                            aria-checked={viewType === 'list'}
                        >
                            <ViewListIcon fontSize="inherit" />
                            <span>목록</span>
                        </button>
                        <button 
                            type="button" 
                            className={`switcher-btn ${viewType === 'calendar' ? 'active' : ''}`}
                            onClick={() => handleViewChange('calendar')}
                            role="radio"
                            aria-checked={viewType === 'calendar'}
                        >
                            <CalendarMonthIcon fontSize="inherit" />
                            <span>달력</span>
                        </button>
                    </div>
                </div>

                {viewType === "list" ? (
                  <>
                <Tab
                    tabs={tabs}
                    defaultActiveTab={activeTab}
                    onTabChange={handleTabChange}
                    marginTop={0}
                    className="admin"
                />

                {/* 완료 탭일 때 월별/강사 필터 표시 */}
                {activeTab === 3 && (
                    <>
                        <WarningBanner
                            message="관리자는 강사명을 검색하여 해당 강사의 스케쥴을 조회할 수 있습니다."
                            margin={`20px`}
                        />
                        <div className="month-selector-wrap">
                            <label htmlFor="month-selector">
                                조회 필터
                            </label>
                            <div className="filter-inputs">
                                <select
                                    id="month-selector"
                                    className="month-selector"
                                    value={targetMonth}
                                    onChange={handleMonthChange}
                                    aria-label="월 선택"
                                >
                                    {monthOptions.map((option) => (
                                        <option
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <input
                                    className="instructor-search-input"
                                    placeholder="강사명 검색"
                                    value={targetInstructor}
                                    onChange={handleInstructorChange}
                                    aria-label="강사 검색"
                                ></input>
                            </div>
                        </div>
                    </>
                )}

                <div className="admin-attendance-wrap">
                    {displayedClasses.length === 0 ? (
                        <div className="empty-state">
                            {activeTab === 1 && "등록된 수업이 없습니다."}
                            {activeTab === 2 &&
                                "출석 처리가 필요한 수업이 없습니다."}
                            {activeTab === 3 &&
                                "출석 처리 완료된 수업이 없습니다."}
                        </div>
                    ) : (
                        displayedClasses.map((sch) => (
                            <AttendanceCard
                                key={sch.id}
                                sch={sch}
                                isCompleted={activeTab === 3}
                                onDeleted={(deletedId) =>
                                    setClassList((prev) =>
                                        prev.filter((c) => c.id !== deletedId)
                                    )
                                }
                                onConverted={refreshScheduleList}
                            />
                        ))
                    )}
                </div>
                  </>
                ) : (
                  <div className="attendance-calendar-view">
                    <div className="calendar-wrap">
                      <Calendar
                        className="buccl-calendar"
                        locale="ko-KR"
                        calendarType="gregory"
                        prevLabel="〈"
                        nextLabel="〉"
                        formatDay={(locale, date) => String(date.getDate())}
                        showNeighboringMonth={false}
                        value={selectedDate}
                        activeStartDate={activeStartDate}
                        onActiveStartDateChange={({ activeStartDate: nextStart }) =>
                          nextStart && setActiveStartDate(nextStart)
                        }
                        onClickDay={(date) => setSelectedDate(date)}
                        onChange={(date) => setSelectedDate(date)}
                        tileClassName={({ date, view }) => {
                          if (view !== "month") return "";
                          const classes = ["tile"];
                          const day = date.getDay();
                          if (day === 0) classes.push("sun");
                          if (day === 6) classes.push("sat");
                          if (getSchedulesByDate(classList, date).length > 0) {
                            classes.push("has-schedule-tile");
                          }
                          return classes.join(" ");
                        }}
                      />
                    </div>
                    <div className="selected-date-header">
                      <div className="label-group">
                        <span className="date-text">
                          {`${dayjs(selectedDate).format("M월 D일")} (${WEEKDAYS_KR[dayjs(selectedDate).day()]})`}
                        </span>
                        <span className="count-text">· {selectedDaySchedules.length}건</span>
                      </div>
                    </div>
                    <div className="admin-attendance-wrap">
                      {selectedDaySchedules.length === 0 ? (
                        <div className="empty-state">
                          등록된 수업이 없습니다.
                        </div>
                      ) : (
                        selectedDaySchedules.map((sch) => (
                          <AttendanceCard
                            key={sch.id}
                            sch={sch}
                            isCompleted={completedIds.has(sch.id)}
                            onDeleted={(deletedId) =>
                              setClassList((prev) =>
                                prev.filter((c) => c.id !== deletedId)
                              )
                            }
                            onConverted={refreshScheduleList}
                          />
                        ))
                      )}
                    </div>
                  </div>
                )}
                <button className="extended-fab" onClick={() => navigate('/class/create')}>
                    <AddIcon />
                    <span>수업 개설</span>
                </button>
            </div>
        </>
    );
};

export default AdminAttendance;
